import type { MeterDimension } from "@orbin/contracts";
import { useQuery } from "@tanstack/react-query";
import { useBilling } from "../components/SubscriptionGate";
import { rpc } from "./rpc";

export type AllowanceLevel = "ok" | "warn" | "full";

export type Allowance = {
  planName: string | null;
  /** Whole percent of the tightest allowance used this period, 0 to 100. */
  percent: number;
  level: AllowanceLevel;
  dimension: MeterDimension;
  periodEnd: string;
};

/** Amber from 80% used, red when nothing is left. */
export function allowanceLevel(fraction: number): AllowanceLevel {
  if (fraction >= 1) return "full";
  return fraction >= 0.8 ? "warn" : "ok";
}

/** How much of this period's allowance is used; null when nothing is capped or metered. */
export function useAllowance(): Allowance | null {
  const { status } = useBilling();
  const query = useQuery({
    queryKey: ["allowance"],
    enabled: status !== null,
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const summary = await rpc.billing.usageSummary();
      if (!summary.metered || summary.worstPercent === null) return null;
      let worst = summary.dimensions[0];
      for (const dimension of summary.dimensions) {
        if ((dimension.percent ?? -1) > (worst?.percent ?? -1)) worst = dimension;
      }
      if (!worst) return null;
      return {
        planName: summary.planName,
        percent: summary.worstPercent,
        level: allowanceLevel(summary.worstPercent / 100),
        dimension: worst.dimension,
        periodEnd: summary.periodEnd,
      } satisfies Allowance;
    },
  });
  return query.data ?? null;
}
